import jsPDF from 'jspdf';
import { formatCurrency, formatPercentage, getLenderRateRange } from './financingCalculations';

function getPageWidth(doc) {
  return doc.internal.pageSize.getWidth
    ? doc.internal.pageSize.getWidth()
    : doc.internal.pageSize.width;
}

function getPageHeight(doc) {
  return doc.internal.pageSize.getHeight
    ? doc.internal.pageSize.getHeight()
    : doc.internal.pageSize.height;
}

export function exportFinancingPdf({
  lender,
  loanAmount,
  amortizationMonths,
  calculation,
}) {
  if (!lender || !calculation) return false;

  const doc = new jsPDF('p', 'pt', 'letter');
  const pageWidth = getPageWidth(doc);
  const pageHeight = getPageHeight(doc);
  const marginX = 44;
  const rightX = pageWidth - marginX;
  const maxTextWidth = pageWidth - marginX * 2;
  const lineHeight = 16;
  let y = 54;

  const ensureSpace = (requiredHeight = 40) => {
    if (y + requiredHeight > pageHeight - 44) {
      doc.addPage();
      y = 54;
    }
  };

  const addSectionTitle = (title) => {
    ensureSpace(26);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(title, marginX, y);
    y += 10;
    doc.setDrawColor(210, 214, 220);
    doc.line(marginX, y, rightX, y);
    y += 16;
  };

  const addKeyValue = (label, value) => {
    ensureSpace(lineHeight);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`${label}:`, marginX, y);
    doc.setFont('helvetica', 'normal');
    doc.text(String(value), marginX + 140, y);
    y += lineHeight;
  };

  const lenderName = lender.name || 'Selected Lender';
  const lenderBands = lender.rateBands || lender.rate_bands || [];
  const contactName = lender.contactName || lender.contact_name || 'N/A';
  const contactEmail = lender.contactEmail || lender.contact_email || 'N/A';
  const generatedAt = new Date();
  const generatedAtText = generatedAt.toLocaleString();
  const amortizationYears = (amortizationMonths / 12).toFixed(0);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('Financing Analysis Report', marginX, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Generated ${generatedAtText}`, rightX, y, { align: 'right' });
  y += 12;
  doc.setDrawColor(150, 160, 175);
  doc.line(marginX, y, rightX, y);
  y += 22;

  addSectionTitle('Lender Profile');
  addKeyValue('Lender', lenderName);
  addKeyValue('Rate Range', getLenderRateRange(lender));
  addKeyValue('Contact', contactName);
  addKeyValue('Email', contactEmail);

  if (lender.description) {
    ensureSpace(40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const descriptionLines = doc.splitTextToSize(lender.description, maxTextWidth);
    doc.text(descriptionLines, marginX, y);
    y += descriptionLines.length * 12 + 6;
  }

  addSectionTitle('Loan Assumptions');
  addKeyValue('Loan Amount', formatCurrency(loanAmount));
  addKeyValue('Amortization', `${amortizationMonths} months (${amortizationYears} years)`);
  addKeyValue('Effective Interest Rate', formatPercentage(calculation.interestRate));

  addSectionTitle('Summary Metrics');
  const boxTop = y - 4;
  const boxHeight = 86;
  ensureSpace(boxHeight + 10);
  doc.setDrawColor(220, 224, 229);
  doc.rect(marginX, boxTop, rightX - marginX, boxHeight);

  const col1 = marginX + 14;
  const col2 = marginX + (rightX - marginX) / 2 + 4;
  doc.setFontSize(10);

  doc.setFont('helvetica', 'bold');
  doc.text('Monthly Payment', col1, y + 16);
  doc.text('Total Cost of Loan', col2, y + 16);
  doc.setFont('helvetica', 'normal');
  doc.text(formatCurrency(calculation.monthlyPayment), col1, y + 34);
  doc.text(formatCurrency(calculation.totalCost), col2, y + 34);

  doc.setFont('helvetica', 'bold');
  doc.text('Total Interest Paid', col1, y + 56);
  doc.text('Interest Rate', col2, y + 56);
  doc.setFont('helvetica', 'normal');
  doc.text(formatCurrency(calculation.totalInterest), col1, y + 74);
  doc.text(formatPercentage(calculation.interestRate), col2, y + 74);
  y += boxHeight + 16;

  addSectionTitle('Yearly Amortization Schedule');
  ensureSpace(24);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Year', marginX, y);
  doc.text('Principal', marginX + 55, y);
  doc.text('Interest', marginX + 165, y);
  doc.text('Total Payment', marginX + 260, y);
  doc.text('Remaining Balance', marginX + 380, y);
  y += 8;
  doc.setDrawColor(210, 214, 220);
  doc.line(marginX, y, rightX, y);
  y += 14;

  doc.setFont('helvetica', 'normal');
  calculation.schedule.forEach((year) => {
    ensureSpace(16);
    doc.text(String(year.year), marginX, y);
    doc.text(formatCurrency(year.totalPrincipal), marginX + 55, y);
    doc.text(formatCurrency(year.totalInterest), marginX + 165, y);
    doc.text(formatCurrency(year.totalPayment), marginX + 260, y);
    doc.text(formatCurrency(year.remainingBalance), marginX + 380, y);
    y += 14;
  });

  if (lenderBands.length > 0) {
    y += 8;
    addSectionTitle('Lender Rate Bands');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);

    lenderBands.forEach((band) => {
      ensureSpace(16);
      const lower = band.loanAmountLowerBand || band.loan_amount_lower_band || 0;
      const upper = band.loanAmountUpperBand || band.loan_amount_upper_band || 0;
      const variable = band.variableInterestRate || band.variable_interest_rate || 0;
      const total = (lender.roi || 0) + variable;
      const line = `${formatCurrency(lower)} - ${formatCurrency(upper)}    |    Total Rate ${formatPercentage(total)}`;
      doc.text(line, marginX, y);
      y += 14;
    });
  }

  ensureSpace(32);
  y += 8;
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  const disclaimer = 'This report provides an estimate for planning purposes only. Final financing terms depend on lender underwriting and approval.';
  const disclaimerLines = doc.splitTextToSize(disclaimer, maxTextWidth);
  doc.text(disclaimerLines, marginX, y);

  const dateToken = generatedAt.toISOString().split('T')[0];
  doc.save(`financing-report-${dateToken}.pdf`);
  return true;
}

